import { useRef, useEffect, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useSetAtom } from 'jotai'

import { pingAtom, timeOffsetAtom } from '../../store/runner.store'
import { toastAtom } from '../../store/toast.store'
import * as logger from '../../utils/logger.ts'

import { connectionCheckerQueryParams } from './api/query-params.ts'

const DEFAULT_DELAY = 10_000 // Regular ping interval
const WARN_TIMEOUT = 2_000 // Show warning if ping takes longer than this
const ERROR_TIMEOUT = 5_000 // Consider ping failed after this time
const BACKOFF_AFTER_RETRIES = 10 // Start increasing delay after this many retries
const STOP_AFTER_RETRIES = 30 // Stop retrying after this many attempts
const SLOW_WARNING_AFTER_PINGS = 1 // Only show slow warning after this many pings

const pingApi = axios.create({
  timeout: ERROR_TIMEOUT,
  baseURL: import.meta.env.VITE_RUNNER_FUNCTION_BASE,
})

type PingResponse = {
  time: number
}

/**
 * ConnectionChecker monitors network connectivity and synchronizes client time
 * with server. It periodically pings the server to: 1. Monitor connection
 * health and show appropriate status toasts 2. Measure network latency 3.
 * Synchronize client clock with server time
 */
export const ConnectionChecker = () => {
  const addToast = useSetAtom(toastAtom)
  const setPing = useSetAtom(pingAtom)
  const setTimeOffset = useSetAtom(timeOffsetAtom)

  // Refs to keep mutable values across retries
  const retriesRef = useRef(0)
  const pingCountRef = useRef(0)
  const lowestPingRef = useRef<number>(Number.MAX_SAFE_INTEGER)
  const connSlowToastRef = useRef<ReturnType<typeof addToast> | null>(null)
  const connLostToastRef = useRef<ReturnType<typeof addToast> | null>(null)
  const connFinalToastRef = useRef<ReturnType<typeof addToast> | null>(null)
  const slowWarningTimer = useRef<globalThis.NodeJS.Timeout | null>(null)

  const handleConnectionLoss = useCallback(() => {
    setPing(-1)
    if (!connFinalToastRef.current) {
      connFinalToastRef.current = addToast({
        title: "Can't reconnect to the server",
        body: 'Please check your network.',
        type: 'fail',
        duration: -1,
        dismissible: true,
      })
    }
  }, [addToast, setPing])

  const { isPaused } = useQuery({
    queryKey: connectionCheckerQueryParams.ping.queryKey,
    queryFn: async () => {
      const clientStartTime = Date.now()
      slowWarningTimer.current = setTimeout(
        showSlowConnectionWarning,
        WARN_TIMEOUT,
      )

      const res = await pingApi.get<PingResponse>('ping')
      const clientEndTime = Date.now()

      const pingDuration = clientEndTime - clientStartTime
      clearTimeout(slowWarningTimer.current!)

      const serverTime = res.data.time

      pingCountRef.current++
      setPing(pingDuration)
      updateTimeSync(clientStartTime, serverTime, pingDuration)

      if (pingCountRef.current === 1) {
        logger.info('[ConnectionChecker] Initial ping successful', pingDuration)
      }

      if (connLostToastRef.current || connFinalToastRef.current) {
        connLostToastRef.current?.dismiss?.()
        connFinalToastRef.current?.dismiss?.()
        connLostToastRef.current = null
        connFinalToastRef.current = null
        addToast({
          title: 'Reconnected successfully',
          type: 'success',
          duration: 6000,
        })
      }

      retriesRef.current = 0 // Reset on success
      return res.data
    },
    refetchOnWindowFocus: true,
    refetchInterval: () => {
      const multiplier = retriesRef.current >= BACKOFF_AFTER_RETRIES ? 2 : 1

      return DEFAULT_DELAY * multiplier
    },
    refetchIntervalInBackground: false,
    refetchOnReconnect: true,
    retry: (failureCount) => {
      retriesRef.current = failureCount

      if (failureCount > STOP_AFTER_RETRIES) {
        handleConnectionLoss()
        return false
      }

      if (!connLostToastRef.current?.visible?.()) {
        connLostToastRef.current = addToast({
          title: 'Connection issue. Retrying...',
          type: 'fail',
          duration: -1,
          dismissible: true,
        })
      }

      return true
    },
    staleTime: 0,
    gcTime: 0,
  })

  function updateTimeSync(
    clientStart: number,
    serverTime: number,
    pingDuration: number,
  ) {
    if (pingDuration < lowestPingRef.current) {
      lowestPingRef.current = pingDuration
      const approximateServerTime = clientStart + pingDuration / 2
      const offset = serverTime - approximateServerTime
      setTimeOffset(offset)
      logger.info('[ConnectionChecker] Set server time offset', offset)
    }
  }

  function showSlowConnectionWarning() {
    if (
      pingCountRef.current > SLOW_WARNING_AFTER_PINGS &&
      !connLostToastRef.current?.visible?.()
    ) {
      connSlowToastRef.current = addToast({
        title: 'Your connection is slow',
        type: 'warn',
        duration: 6000,
      })
    }
  }

  useEffect(() => {
    if (isPaused) {
      handleConnectionLoss()
    }
  }, [handleConnectionLoss, isPaused])

  return null
}
